// Ordem automática (P0.2):
//  1. em andamento ou pausada no topo
//  2. com prazo, do mais próximo ao mais distante (data + horário)
//  3. no mesmo dia, com horário antes de só data
//  4. sem data no final
//  5. empate: ordem de criação
// Por cima dela, a ordem arrastada pelo usuário (US-007) — ver manualOrder.ts.
import { addDays, diffDays, toDateKey } from './dates';
import { arrange } from './manualOrder';
import { isOverdue, type Task } from './task';

function activeRank(t: Task): number {
  if (t.status === 'running') return 0;
  if (t.status === 'paused') return 1;
  return 2;
}

export function compareTasks(a: Task, b: Task): number {
  const ra = activeRank(a);
  const rb = activeRank(b);
  if (ra !== rb) return ra - rb;
  if (ra === 1) {
    // várias pausadas: a mais recente primeiro
    const byUpdate = b.updated_at.localeCompare(a.updated_at);
    if (byUpdate) return byUpdate;
  }
  if (a.due_date && !b.due_date) return -1;
  if (!a.due_date && b.due_date) return 1;
  if (a.due_date && b.due_date) {
    if (a.due_date !== b.due_date) return a.due_date < b.due_date ? -1 : 1;
    if (a.due_time && !b.due_time) return -1;
    if (!a.due_time && b.due_time) return 1;
    if (a.due_time && b.due_time && a.due_time !== b.due_time) return a.due_time < b.due_time ? -1 : 1;
  }
  return a.created_at.localeCompare(b.created_at);
}

/** Tarefas abertas na ordem automática (concluídas ficam de fora). */
export function sortTasks(tasks: readonly Task[]): Task[] {
  return tasks.filter((t) => t.status !== 'done').sort(compareTasks);
}

/** Atrasadas · Hoje · Amanhã · Próximos dias (2 a 7) · Para depois (depois de 7 dias ou sem data) — ASM-007 */
export type SectionKey = 'overdue' | 'today' | 'tomorrow' | 'next7' | 'later';

export interface Section {
  key: SectionKey;
  tasks: Task[];
}

export interface TaskBoard {
  /** Tarefa rodando ou, se nenhuma roda, a pausada mais recente. */
  current: Task | null;
  sections: Section[];
  /** Concluídas no dia local atual, da mais recente para a mais antiga (ASM-004). */
  doneToday: Task[];
  /** Próxima da fila (primeira tarefa parada, na ordem da lista — automática + arrastada). */
  next: Task | null;
}

export const SECTION_ORDER: SectionKey[] = ['overdue', 'today', 'tomorrow', 'next7', 'later'];

export function sectionOf(task: Pick<Task, 'due_date' | 'due_time' | 'status'>, now: Date): SectionKey {
  if (!task.due_date) return 'later';
  if (isOverdue(task as Task, now)) return 'overdue';
  const today = toDateKey(now);
  const days = diffDays(today, task.due_date);
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  // "Próximos dias" = de 2 a 7 dias à frente; depois disso (ou sem data), "Para depois" (ASM-007).
  if (task.due_date <= addDays(today, 7)) return 'next7';
  return 'later';
}

export function buildBoard(tasks: readonly Task[], now: Date): TaskBoard {
  const open = sortTasks(tasks);
  const current = open.find((t) => t.status === 'running') ?? open.find((t) => t.status === 'paused') ?? null;
  const rest = open.filter((t) => t !== current);

  const buckets = new Map<SectionKey, Task[]>();
  for (const t of rest) {
    const key = sectionOf(t, now);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(t);
  }
  // Seção só aparece se tiver tarefa — "Atrasadas" só existe com atraso (AC-009).
  // dentro de cada seção, a ordem arrastada vale por cima da automática
  const sections = SECTION_ORDER.filter((k) => buckets.has(k)).map((key) => ({
    key,
    tasks: arrange(buckets.get(key)!),
  }));

  const today = toDateKey(now);
  const doneToday = tasks
    .filter((t) => t.status === 'done' && t.completed_at && toDateKey(new Date(t.completed_at)) === today)
    .sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? ''));

  const next = sections.flatMap((x) => x.tasks).find((t) => t.status === 'todo' || t.status === 'paused') ?? null;
  return { current, sections, doneToday, next };
}

/** Pendentes para hoje (atrasadas + hoje) — usada na mensagem de fim do dia. */
export function pendingForToday(tasks: readonly Task[], now: Date): Task[] {
  return tasks.filter((t) => {
    if (t.status === 'done') return false;
    const s = sectionOf(t, now);
    return s === 'overdue' || s === 'today';
  });
}
